import { Injectable } from '@angular/core';
import { NumberUtils } from '@modeso/aumico-lib-core-fe';
import {
    IAccount,
    IAppropriationOfProfit,
    IDisplayOptions,
    IFinStatements,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IPrintPreviewMappingValue } from '../../../interfaces/IPrintPreviewMappingValue.interface';

@Injectable({ providedIn: 'root' })
export class AopAdjustmentsAdnReclassesHelper {
    public calculateCurrentAnnualProfitWithAdjustmentsAndReclasses(
        incomeFinancialStatement: IFinStatements,
        options: IDisplayOptions,
    ): number | null {
        if (
            incomeFinancialStatement == null ||
            incomeFinancialStatement.templateAndAccountsMapping == null ||
            incomeFinancialStatement.templateAndAccountsMapping.template == null
        ) {
            return null;
        }
        const incomeTemplate = incomeFinancialStatement.templateAndAccountsMapping.template;

        if (incomeTemplate.group.calculatedClosingBalance == null) {
            return null;
        }

        let calculatedCurrentAnnualProfit = NumberUtils.roundNumber(incomeTemplate.group.calculatedClosingBalance);

        if (
            options.showReClassInSums &&
            incomeTemplate.group.closingReClass != null &&
            NumberUtils.isFinite(incomeTemplate.group.closingReClass)
        ) {
            calculatedCurrentAnnualProfit = NumberUtils.sumNumbers([
                calculatedCurrentAnnualProfit,
                incomeTemplate.group.closingReClass,
            ]);
        }

        if (
            options.showAdjustmentInSums &&
            incomeTemplate.group.closingAdjustment != null &&
            NumberUtils.isFinite(incomeTemplate.group.closingAdjustment)
        ) {
            calculatedCurrentAnnualProfit = NumberUtils.sumNumbers([
                calculatedCurrentAnnualProfit,
                incomeTemplate.group.closingAdjustment,
            ]);
        }

        return calculatedCurrentAnnualProfit;
    }

    public calculatePreviousAnnualProfitWithAdjustmentsAndReclasses(
        incomeFinancialStatement: IFinStatements,
        options: IDisplayOptions,
    ): number | null {
        if (
            incomeFinancialStatement == null ||
            incomeFinancialStatement.templateAndAccountsMapping == null ||
            incomeFinancialStatement.templateAndAccountsMapping.template == null
        ) {
            return null;
        }
        const incomeTemplate = incomeFinancialStatement.templateAndAccountsMapping.template;

        if (incomeTemplate.group.calculatedOpeningBalance == null) {
            return null;
        }

        let calculatedPreviousAnnualProfit = NumberUtils.roundNumber(incomeTemplate.group.calculatedOpeningBalance);

        if (
            options.showReClassInSums &&
            incomeTemplate.group.openingReClass != null &&
            NumberUtils.isFinite(incomeTemplate.group.openingReClass)
        ) {
            calculatedPreviousAnnualProfit = NumberUtils.sumNumbers([
                calculatedPreviousAnnualProfit,
                incomeTemplate.group.openingReClass,
            ]);
        }

        if (
            options.showAdjustmentInSums &&
            incomeTemplate.group.openingAdjustment != null &&
            NumberUtils.isFinite(incomeTemplate.group.openingAdjustment)
        ) {
            calculatedPreviousAnnualProfit = NumberUtils.sumNumbers([
                calculatedPreviousAnnualProfit,
                incomeTemplate.group.openingAdjustment,
            ]);
        }

        return calculatedPreviousAnnualProfit;
    }

    public calculateSelectedAccountWithAdjustmentsAndReclasses(
        fliIdToAccounts: Map<string, IAccount[]>,
        appropriationOfProfit: IAppropriationOfProfit,
        options: IDisplayOptions,
    ): IPrintPreviewMappingValue {
        const selectedAccountPrintPreviewMappingValue: IPrintPreviewMappingValue = {
            openingValue: null,
            closingValue: null,
        };
        if (
            appropriationOfProfit == null ||
            appropriationOfProfit.profitCalculationOutput == null ||
            appropriationOfProfit.profitCalculationOutput.account == null
        ) {
            return selectedAccountPrintPreviewMappingValue;
        }

        const selectedProfitAccount = appropriationOfProfit.profitCalculationOutput.account;

        if (
            selectedProfitAccount.calculatedClosingBalance == null &&
            selectedProfitAccount.calculatedClosingBalance == null
        ) {
            return selectedAccountPrintPreviewMappingValue;
        }

        if (!options.showAdjustmentInSums && !options.showReClassInSums) {
            if (selectedProfitAccount.calculatedClosingBalance != null) {
                selectedAccountPrintPreviewMappingValue.closingValue = NumberUtils.roundNumber(
                    selectedProfitAccount.calculatedClosingBalance,
                );
            }
            if (selectedProfitAccount.calculatedOpeningBalance != null) {
                selectedAccountPrintPreviewMappingValue.openingValue = NumberUtils.roundNumber(
                    selectedProfitAccount.calculatedOpeningBalance,
                );
            }
            return selectedAccountPrintPreviewMappingValue;
        }

        for (const accounts of fliIdToAccounts.values()) {
            const foundSelectedAccount = accounts.find((account) =>
                this.isAccountMatch(selectedProfitAccount, account),
            );
            if (foundSelectedAccount != null) {
                return this.calculateAccountWithAdjustmentsAndReclasses(foundSelectedAccount, options);
            }
        }

        return selectedAccountPrintPreviewMappingValue;
    }

    private isAccountMatch(profitSelectAccount: IAccount, templateAccount: IAccount): boolean {
        return (
            profitSelectAccount.accountNumber === templateAccount.accountNumber &&
            profitSelectAccount.name === templateAccount.name &&
            profitSelectAccount.closingBalance === templateAccount.closingBalance &&
            profitSelectAccount.openingBalance === templateAccount.openingBalance
        );
    }

    public calculateAccountWithAdjustmentsAndReclasses(
        account: IAccount,
        options: IDisplayOptions,
    ): IPrintPreviewMappingValue {
        const selectedAccountPrintPreviewMappingValue: IPrintPreviewMappingValue = {
            openingValue: null,
            closingValue: null,
        };
        if (account.calculatedClosingBalance != null && NumberUtils.isFinite(account.calculatedClosingBalance)) {
            selectedAccountPrintPreviewMappingValue.closingValue = NumberUtils.roundNumber(
                account.calculatedClosingBalance,
            );
        }
        if (account.calculatedOpeningBalance != null && NumberUtils.isFinite(account.calculatedOpeningBalance)) {
            selectedAccountPrintPreviewMappingValue.openingValue = NumberUtils.roundNumber(
                account.calculatedOpeningBalance,
            );
        }

        if (
            options.showReClassInSums &&
            account.closingReClass != null &&
            NumberUtils.isFinite(account.closingReClass) &&
            selectedAccountPrintPreviewMappingValue.closingValue != null
        ) {
            selectedAccountPrintPreviewMappingValue.closingValue = NumberUtils.sumNumbers([
                selectedAccountPrintPreviewMappingValue.closingValue,
                account.closingReClass,
            ]);
        }

        if (
            options.showReClassInSums &&
            account.openingReClass != null &&
            NumberUtils.isFinite(account.openingReClass) &&
            selectedAccountPrintPreviewMappingValue.openingValue != null
        ) {
            // Was reading `closingReClass` — copy-paste bug, fixed to
            // `openingReClass`. Same field name pattern as the closingValue
            // branch above, just for the opening side.
            selectedAccountPrintPreviewMappingValue.openingValue = NumberUtils.sumNumbers([
                selectedAccountPrintPreviewMappingValue.openingValue,
                account.openingReClass,
            ]);
        }

        if (
            options.showAdjustmentInSums &&
            account.closingAdjustment != null &&
            NumberUtils.isFinite(account.closingAdjustment) &&
            selectedAccountPrintPreviewMappingValue.closingValue != null
        ) {
            selectedAccountPrintPreviewMappingValue.closingValue = NumberUtils.sumNumbers([
                selectedAccountPrintPreviewMappingValue.closingValue,
                account.closingAdjustment,
            ]);
        }

        if (
            options.showAdjustmentInSums &&
            account.openingAdjustment != null &&
            NumberUtils.isFinite(account.openingAdjustment) &&
            selectedAccountPrintPreviewMappingValue.openingValue != null
        ) {
            // Was reading `closingAdjustment` — same copy-paste bug; fixed
            // to `openingAdjustment`.
            selectedAccountPrintPreviewMappingValue.openingValue = NumberUtils.sumNumbers([
                selectedAccountPrintPreviewMappingValue.openingValue,
                account.openingAdjustment,
            ]);
        }

        return selectedAccountPrintPreviewMappingValue;
    }
}
